import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import {
  ArrowDownUp, Check, Copy, Download, ExternalLink, Globe, History, Link2, Lock,
  Mail, MousePointerClick, Nfc, Plus, Printer, QrCode, RefreshCw, Search, Share2,
  Tag, Users,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, navigate } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from '../admin/primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { LiveNumber, QuietNumber } from '../soirees/lib.js'
import { InsightsChart } from './InstagramChart.js'
import { PERIODES, fraicheurFr, nombreFr, estErreurApi, type CommunicationPeriod } from './lib.js'

/**
 * ACQUISITION CLUB — Liens & QR/NFC (V1).
 *
 * Chaque lien est un SUPPORT RÉEL du club (carte NFC au bar, QR à
 * l'accueil, lien d'emailing) — pas une ligne de DataGrid. Le tracker
 * insta.liveclubrennes.com reste la source de vérité : Gestion lit son
 * miroir PostgreSQL et POSE des commandes que le worker exécute.
 *
 * Doctrine héritée du contrat api-v1.md :
 *  · « navigateurs uniques », jamais « personnes » ; jamais additionnés ;
 *  · NULL (pas encore renseigné) ≠ UNKNOWN_LEGACY (jamais déterminable) ;
 *  · slug immuable après création ; racine ni désactivable ni supprimable ;
 *  · un lien avec historique se DÉSACTIVE, il ne se supprime pas.
 * AUCUN drawer. Modales : confirmations uniquement.
 */

/* ═══════════════════════════ types & aides ═══════════════════════════ */

export interface LienAcq {
  slug: string
  label: string
  url: string
  public_url: string
  active: boolean
  is_root: boolean
  source: string | null
  placement: string | null
  asset: string | null
  campaign_default: string | null
  provider_created_at: string
  provider_updated_at: string
  clicks: number
  visitors: number
  last_click_at: string | null
  synced_at: string
}

interface ReponseLiens {
  links: LienAcq[]
  sources: string[]
  source_labels: Record<string, string>
  health: { status: string; detail: string | null; checked_at: string | null }
  last_links_sync_at: string | null
  last_sync_ok_at: string | null
  write_configured: boolean
}

export const SOURCE_ICONS: Record<string, typeof QrCode> = {
  QR: QrCode, NFC: Nfc, WEB: Globe, EMAIL: Mail, SOCIAL: Share2,
  PRINT: Printer, OTHER: Tag, UNKNOWN_LEGACY: History,
}

export function SourceBadge({ source, labels }: { source: string | null; labels: Record<string, string> }) {
  if (source === null) {
    // §46 : pas d'alerte rouge — une invitation, pas un reproche.
    return <span className="lc-pill lc-pill--idle acq-badge">À CLASSIFIER</span>
  }
  const Icon = SOURCE_ICONS[source] ?? Tag
  return (
    <span className="lc-pill acq-badge acq-badge--src">
      <Icon size={11} aria-hidden="true" /> {labels[source] ?? source}
    </span>
  )
}

/** Normalisation de l'adresse courte — MIROIR de la règle provider (§55).
 *  Le tracker reste l'autorité : ceci sert uniquement l'aperçu. */
export function slugifier(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
}

/** Pose une commande et attend son verdict (le worker la réclame en ≤ 10 s). */
export async function executerCommande(
  action: string, payload: Record<string, unknown>,
): Promise<{ ok: boolean; status: string; error_code: string | null; human_error: string | null; result: Record<string, unknown> | null }> {
  const r = await adminMutate<{ error?: string; message?: string; command_id?: string }>(
    'POST', '/api/v1/communication/acquisition/commands', { action, payload })
  if (r.status !== 202 || !r.data.command_id) {
    return { ok: false, status: 'REFUSEE', error_code: r.data.error ?? null, human_error: r.data.message ?? 'Commande refusée.', result: null }
  }
  for (let i = 0; i < 45; i++) {
    await new Promise((res) => setTimeout(res, i < 5 ? 700 : 1500))
    const c = await apiGet<{ command?: { status: string; provider_error_code: string | null; human_error: string | null; provider_result: Record<string, unknown> | null } }>(
      `/api/v1/communication/acquisition/commands/${r.data.command_id}`)
    const cmd = (c as { command?: { status: string; provider_error_code: string | null; human_error: string | null; provider_result: Record<string, unknown> | null } }).command
    if (cmd && cmd.status !== 'PENDING' && cmd.status !== 'RUNNING') {
      return { ok: cmd.status === 'SUCCEEDED', status: cmd.status, error_code: cmd.provider_error_code, human_error: cmd.human_error, result: cmd.provider_result }
    }
  }
  return { ok: false, status: 'TIMEOUT', error_code: null, human_error: 'Le worker n’a pas répondu à temps — la commande reste visible dans le diagnostic.', result: null }
}

function BoutonCopier({ texte, label = 'Copier' }: { texte: string; label?: string }) {
  const [copie, setCopie] = useState(false)
  return (
    <button type="button" className="lc-btn lc-btn--ghost acq-copy" onClick={async () => {
      try { await navigator.clipboard.writeText(texte); setCopie(true); setTimeout(() => setCopie(false), 1800) } catch { /* refus navigateur */ }
    }}>
      {copie ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
      {copie ? 'Copié' : label}
    </button>
  )
}

/** §86 : tracker muet ≠ zéro clic. Les données locales restent, on le dit. */
function BandeauDegrade({ health, lastOk }: { health: ReponseLiens['health'] | null; lastOk: string | null }) {
  if (!health || health.status === 'OK') return null
  return (
    <p className="cm-safenote acq-degrade">
      {health.status === 'VERSION_INCOMPATIBLE'
        ? 'Le tracker annonce une version d’API incompatible : les modifications sont bloquées, l’historique local reste affiché.'
        : 'Le tracker est momentanément injoignable : les chiffres affichés sont les dernières données synchronisées, les modifications sont indisponibles.'}
      {lastOk ? <> Dernière synchronisation réussie {fraicheurFr(lastOk)}.</> : null}
    </p>
  )
}

/* ═══════════════════════ PAGE LISTE — /communication/liens ═══════════════ */

type Filtre = 'TOUS' | 'QR' | 'NFC' | 'WEB' | 'EMAIL' | 'SOCIAL' | 'PRINT' | 'OTHER' | 'NON_CLASSES' | 'INACTIFS'
type Tri = 'CLICS' | 'DERNIER' | 'RECENT' | 'NOM'

const FILTRES: { value: Filtre; label: string }[] = [
  { value: 'TOUS', label: 'Tous' }, { value: 'NFC', label: 'NFC' }, { value: 'QR', label: 'QR Code' },
  { value: 'WEB', label: 'Web' }, { value: 'EMAIL', label: 'Email' }, { value: 'SOCIAL', label: 'Social' },
  { value: 'PRINT', label: 'Imprimé' }, { value: 'OTHER', label: 'Autre' },
  { value: 'NON_CLASSES', label: 'Non classés' }, { value: 'INACTIFS', label: 'Inactifs' },
]
const TRIS: { value: Tri; label: string }[] = [
  { value: 'CLICS', label: 'Plus de clics' }, { value: 'DERNIER', label: 'Dernier clic' },
  { value: 'RECENT', label: 'Plus récent' }, { value: 'NOM', label: 'Nom' },
]

function CarteLien({ l, labels }: { l: LienAcq; labels: Record<string, string> }) {
  return (
    <Link href={`/communication/liens/${l.slug}`} className={`lc-card acq-card lc-in ${l.active ? '' : 'acq-card--off'}`}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="acq-card__tete">
        <SourceBadge source={l.source} labels={labels} />
        {l.is_root ? <span className="lc-pill lc-pill--idle acq-badge">Lien racine</span> : null}
        <span className={`lc-pill acq-badge ${l.active ? 'lc-pill--ok' : 'lc-pill--warning'}`}>
          {l.active ? 'Actif' : 'Inactif'}
        </span>
      </div>
      <h3 className="acq-card__nom">{l.label}</h3>
      {l.placement ? <span className="lc-hint acq-card__place">{l.placement}{l.asset ? ` · ${l.asset}` : ''}</span> : null}
      <span className="acq-card__url">{l.public_url.replace('https://', '')}</span>
      <div className="acq-card__kpis">
        <span><strong>{nombreFr(l.clicks)}</strong> clics</span>
        <span><strong>{nombreFr(l.visitors)}</strong> navigateurs uniques</span>
      </div>
      <span className="lc-hint acq-card__dernier">
        {l.last_click_at ? `Dernier clic ${fraicheurFr(l.last_click_at)}` : 'Aucun clic pour l’instant'}
      </span>
      {l.source === null ? <span className="acq-card__cta">Renseigner le support →</span> : null}
    </Link>
  )
}

function ListeContent() {
  const [data, setData] = useState<ReponseLiens | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  // Entrée directe depuis le widget Instagram : ?filtre=non-classes
  const [filtre, setFiltre] = useState<Filtre>(() =>
    new URLSearchParams(window.location.search).get('filtre') === 'non-classes' ? 'NON_CLASSES' : 'TOUS')
  const [recherche, setRecherche] = useState('')
  const [tri, setTri] = useState<Tri>('CLICS')
  const [syncBusy, setSyncBusy] = useState(false)
  const toast = useToast()
  const permissions = usePermissions()
  const gere = permissions.permissions.includes('communication.settings.manage')

  const charger = useCallback(() => {
    void apiGet<ReponseLiens | { error: string; message?: string }>('/api/v1/communication/acquisition/liens')
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setErreur(null); setData(j) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [])
  useEffect(() => { charger() }, [charger])

  const liens = useMemo(() => {
    if (!data) return []
    let out = data.links
    if (filtre === 'INACTIFS') out = out.filter((l) => !l.active)
    else if (filtre === 'NON_CLASSES') out = out.filter((l) => l.source === null)
    else if (filtre !== 'TOUS') out = out.filter((l) => l.source === filtre)
    const q = recherche.trim().toLowerCase()
    if (q) {
      out = out.filter((l) => [l.label, l.slug, l.placement ?? '', l.asset ?? ''].some((v) => v.toLowerCase().includes(q)))
    }
    return [...out].sort((a, b) => {
      switch (tri) {
        case 'CLICS': return b.clicks - a.clicks
        case 'DERNIER': return (b.last_click_at ?? '').localeCompare(a.last_click_at ?? '')
        case 'RECENT': return b.provider_created_at.localeCompare(a.provider_created_at)
        case 'NOM': return a.label.localeCompare(b.label, 'fr')
      }
    })
  }, [data, filtre, recherche, tri])

  const nonClasses = data?.links.filter((l) => l.source === null).length ?? 0

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Liens &amp; QR/NFC</h1>
          <p className="lc-subtitle">
            Les supports physiques et numériques qui amènent vers Instagram —
            comptés par insta.liveclubrennes.com.
            {data?.last_links_sync_at ? <span className="lc-hint"> · liens synchronisés {fraicheurFr(data.last_links_sync_at)}</span> : null}
          </p>
        </div>
        <div className="cm-head__actions">
          {gere ? (
            <button type="button" className="lc-btn" disabled={syncBusy} onClick={async () => {
              setSyncBusy(true)
              const r = await executerCommande('SYNC_NOW', {})
              setSyncBusy(false)
              if (r.ok) { toast('ok', 'Synchronisation terminée.'); charger() }
              else toast('error', r.human_error ?? 'Synchronisation impossible.')
            }}>
              <RefreshCw size={14} aria-hidden="true" className={syncBusy ? 'cm-spin' : ''} />
              {syncBusy ? 'Synchronisation…' : 'Synchroniser'}
            </button>
          ) : null}
          {gere ? (
            <Link href="/communication/liens/nouveau" className="lc-btn lc-btn--primary">
              <Plus size={14} aria-hidden="true" /> Nouveau lien
            </Link>
          ) : null}
        </div>
      </header>

      <BandeauDegrade health={data?.health ?? null} lastOk={data?.last_sync_ok_at ?? null} />
      {erreur ? <p className="lc-hint">{erreur}</p> : null}

      <div className="acq-outils">
        <div className="lc-seg acq-filtres" role="group" aria-label="Filtrer par support">
          {FILTRES.map((f) => (
            <button key={f.value} type="button"
              className={`lc-seg__btn ${filtre === f.value ? 'lc-seg__btn--on' : ''}`}
              aria-pressed={filtre === f.value}
              onClick={() => setFiltre(f.value)}>
              {f.label}{f.value === 'NON_CLASSES' && nonClasses > 0 ? ` (${nonClasses})` : ''}
            </button>
          ))}
        </div>
        <div className="acq-outils__droite">
          <span className="acq-recherche">
            <Search size={14} aria-hidden="true" />
            <input className="lc-input" value={recherche} placeholder="Rechercher un support…"
              onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher un lien" />
          </span>
          <label className="acq-tri">
            <ArrowDownUp size={13} aria-hidden="true" />
            <select className="lc-input" value={tri} onChange={(e) => setTri(e.target.value as Tri)} aria-label="Trier">
              {TRIS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {data && liens.length === 0 ? (
        <p className="lc-hint">Aucun lien ne correspond{recherche ? ' à cette recherche' : ''}.</p>
      ) : null}
      <div className="acq-grid lc-stagger">
        {liens.map((l) => <CarteLien key={l.slug} l={l} labels={data?.source_labels ?? {}} />)}
      </div>
    </>
  )
}

export function AcquisitionLinksPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <ToastProvider><ListeContent /></ToastProvider>
      </main>
    </Shell>
  )
}

/* ═══════════════ PAGE DÉTAIL — /communication/liens/:slug ═══════════════ */

interface DetailStats {
  clicks: number
  visitors: number
  clicks_7d: number
  clicks_30d: number
  visitors_7d: number
  last_click_at: string | null
  daily: Array<{ day: string; clicks: number; visitors: number }>
  by_campaign: Array<{ campaign: string; clicks: number }>
  by_device: Array<{ device: string | null; clicks: number }>
  by_browser: Array<{ browser: string | null; clicks: number }>
  by_os: Array<{ os: string | null; clicks: number }>
}

function Repartition({ titre, lignes }: { titre: string; lignes: Array<{ k: string; n: number }> }) {
  if (lignes.length === 0) return null
  const max = Math.max(...lignes.map((l) => l.n))
  return (
    <section className="lc-panel acq-panel">
      <header className="lc-panel__head"><h2 className="lc-section-title">{titre}</h2></header>
      <div className="lc-panel__body">
        <ul className="acq-repart">
          {lignes.map((l) => (
            <li key={l.k}>
              <span className="acq-repart__label">{l.k}</span>
              <span className="acq-repart__barre" aria-hidden="true">
                <span style={{ width: `${Math.max(4, (l.n / max) * 100)}%` }} />
              </span>
              <strong>{nombreFr(l.n)}</strong>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function DetailContent({ slug }: { slug: string }) {
  const [link, setLink] = useState<LienAcq | null>(null)
  const [stats, setStats] = useState<DetailStats | null>(null)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [periode, setPeriode] = useState<CommunicationPeriod>('THIRTY_DAYS')
  const [erreur, setErreur] = useState<string | null>(null)
  const [confirme, setConfirme] = useState<'DESACTIVER' | 'ACTIVER' | 'SUPPRIMER' | null>(null)
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const permissions = usePermissions()
  const gere = permissions.permissions.includes('communication.settings.manage')

  const charger = useCallback(() => {
    void apiGet<{ link: LienAcq; stats: DetailStats; source_labels: Record<string, string> } | { error: string; message?: string }>(
      `/api/v1/communication/acquisition/liens/${slug}?period=${periode}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lien introuvable.'); return }
        setErreur(null); setLink(j.link); setStats(j.stats); setLabels(j.source_labels)
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [slug, periode])
  useEffect(() => { charger() }, [charger])

  const action = async (a: 'DESACTIVER' | 'ACTIVER' | 'SUPPRIMER') => {
    setBusy(true)
    const r = a === 'SUPPRIMER'
      ? await executerCommande('DELETE_LINK', { slug })
      : await executerCommande('UPDATE_LINK', { slug, patch: { active: a === 'ACTIVER' } })
    setBusy(false)
    setConfirme(null)
    if (r.ok) {
      if (a === 'SUPPRIMER') { toast('ok', 'Lien supprimé.'); navigate('/communication/liens'); return }
      toast('ok', a === 'ACTIVER' ? 'Lien réactivé.' : 'Lien désactivé — il répond désormais 404 publiquement.')
      charger()
    } else {
      toast('error', r.human_error ?? 'Le tracker a refusé l’opération.')
      charger() // §37 : après un conflit, on repart des dernières informations.
    }
  }

  if (erreur) return <p className="lc-hint">{erreur}</p>
  if (!link) return null

  const qrBase = `/api/v1/communication/acquisition/liens/${link.slug}/qr`
  const supprimable = !link.is_root && link.clicks === 0

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">{link.label}</h1>
          <p className="lc-subtitle acq-detail__sous">
            <SourceBadge source={link.source} labels={labels} />
            {link.placement ? <span className="lc-hint">{link.placement}</span> : null}
            <span className={`lc-pill acq-badge ${link.active ? 'lc-pill--ok' : 'lc-pill--warning'}`}>
              {link.active ? 'ACTIF' : 'INACTIF'}
            </span>
          </p>
          <p className="acq-permanente" title="Adresse permanente — l’adresse courte ne peut pas être renommée : elle est peut-être déjà imprimée ou programmée dans une puce.">
            <Lock size={12} aria-hidden="true" />
            <a href={link.public_url} target="_blank" rel="noreferrer">{link.public_url.replace('https://', '')}</a>
            <ExternalLink size={11} aria-hidden="true" />
            <BoutonCopier texte={link.public_url} label="Copier le lien" />
          </p>
        </div>
        <div className="cm-head__actions">
          <div className="lc-seg cm-periods" role="group" aria-label="Période">
            {PERIODES.map((p) => (
              <button key={p.value} type="button"
                className={`lc-seg__btn ${periode === p.value ? 'lc-seg__btn--on' : ''}`}
                aria-pressed={periode === p.value}
                onClick={() => setPeriode(p.value)}>{p.label}</button>
            ))}
          </div>
          {gere ? <Link href={`/communication/liens/${link.slug}/modifier`} className="lc-btn">Modifier</Link> : null}
          {gere && !link.is_root ? (
            <button type="button" className="lc-btn" disabled={busy}
              onClick={() => setConfirme(link.active ? 'DESACTIVER' : 'ACTIVER')}>
              {link.active ? 'Désactiver' : 'Réactiver'}
            </button>
          ) : null}
          {gere && supprimable ? (
            <button type="button" className="lc-btn lc-btn--danger" disabled={busy} onClick={() => setConfirme('SUPPRIMER')}>
              Supprimer
            </button>
          ) : null}
        </div>
      </header>

      {stats ? (
        <>
          <div className="acq-kpis lc-stagger">
            <div className="lc-card cm-widget acq-kpi lc-in">
              <span className="lc-card__icon" aria-hidden="true"><MousePointerClick /></span>
              <strong className="cm-big__num"><LiveNumber value={stats.clicks} format={nombreFr} /></strong>
              <span className="cm-big__label">clics</span>
            </div>
            <div className="lc-card cm-widget acq-kpi lc-in">
              <span className="lc-card__icon" aria-hidden="true"><Users /></span>
              <strong className="cm-big__num"><LiveNumber value={stats.visitors} format={nombreFr} /></strong>
              <span className="cm-big__label">navigateurs uniques</span>
            </div>
            <div className="lc-card cm-widget acq-kpi lc-in">
              <strong className="acq-kpi__val"><QuietNumber value={stats.clicks_7d} format={nombreFr} /></strong>
              <span className="cm-big__label">7 jours</span>
            </div>
            <div className="lc-card cm-widget acq-kpi lc-in">
              <strong className="acq-kpi__val"><QuietNumber value={stats.clicks_30d} format={nombreFr} /></strong>
              <span className="cm-big__label">30 jours</span>
            </div>
            <div className="lc-card cm-widget acq-kpi lc-in">
              <strong className="acq-kpi__val acq-kpi__val--txt">{stats.last_click_at ? fraicheurFr(stats.last_click_at) : '—'}</strong>
              <span className="cm-big__label">dernier clic</span>
            </div>
          </div>

          <section className="lc-panel acq-panel">
            <header className="lc-panel__head">
              <h2 className="lc-section-title">Clics quotidiens</h2>
              <span className="lc-hint">jours civils Europe/Paris · robots exclus</span>
            </header>
            <div className="lc-panel__body">
              {stats.daily.every((d) => d.clicks === 0)
                ? <p className="lc-hint">Aucun clic sur cette période.</p>
                : <InsightsChart points={stats.daily.map((d) => ({ day: d.day, value: d.clicks }))} height={150} accent="var(--cyan)" />}
            </div>
          </section>

          <div className="acq-deuxcol">
            <Repartition titre="Campagnes" lignes={stats.by_campaign.map((r) => ({ k: r.campaign, n: r.clicks }))} />
            <Repartition titre="Appareils" lignes={stats.by_device.map((r) => ({ k: r.device ?? 'inconnu', n: r.clicks }))} />
            <Repartition titre="Navigateurs" lignes={stats.by_browser.map((r) => ({ k: r.browser ?? 'inconnu', n: r.clicks }))} />
            <Repartition titre="Systèmes" lignes={stats.by_os.map((r) => ({ k: r.os ?? 'inconnu', n: r.clicks }))} />
          </div>
        </>
      ) : null}

      <div className="acq-deuxcol">
        <section className="lc-panel acq-panel">
          <header className="lc-panel__head">
            <h2 className="lc-section-title">QR Code</h2>
            <span className="lc-hint">encode l’adresse publique — jamais la destination finale</span>
          </header>
          <div className="lc-panel__body acq-qr">
            <img src={`${qrBase}.svg`} alt={`QR code vers ${link.public_url}`} className="acq-qr__img" width={164} height={164} />
            <div className="acq-qr__actions">
              <a className="lc-btn" href={`${qrBase}.svg?download=1`}><Download size={14} aria-hidden="true" /> Télécharger SVG</a>
              <a className="lc-btn" href={`${qrBase}.png?download=1`}><Download size={14} aria-hidden="true" /> Télécharger PNG</a>
              <span className="lc-hint">SVG : impression vectorielle · PNG : 1024 px, zone de silence incluse.</span>
            </div>
          </div>
        </section>

        {link.source === 'NFC' || link.source === null ? (
          <section className="lc-panel acq-panel">
            <header className="lc-panel__head">
              <h2 className="lc-section-title">URL à programmer{link.source === 'NFC' ? ' (puce NFC)' : ''}</h2>
            </header>
            <div className="lc-panel__body">
              <p className="acq-nfc">
                <Nfc size={16} aria-hidden="true" />
                <code>{link.public_url}</code>
                <BoutonCopier texte={link.public_url} />
              </p>
              <p className="lc-hint">
                À écrire dans la puce avec votre application NFC habituelle —
                Gestion ne programme pas la puce elle-même.
              </p>
            </div>
          </section>
        ) : null}
      </div>

      <ConfirmDialog open={confirme === 'DESACTIVER'} title="Désactiver ce lien ?"
        confirmLabel="Désactiver" cancelLabel="Garder actif"
        onConfirm={() => void action('DESACTIVER')} onClose={() => setConfirme(null)}>
        <p>Le support répondra 404 publiquement tant qu’il est désactivé. L’historique de clics est conservé, et la réactivation est immédiate.</p>
      </ConfirmDialog>
      <ConfirmDialog open={confirme === 'ACTIVER'} title="Réactiver ce lien ?"
        confirmLabel="Réactiver" onConfirm={() => void action('ACTIVER')} onClose={() => setConfirme(null)}>
        <p>Le support redirigera à nouveau les visiteurs vers sa destination.</p>
      </ConfirmDialog>
      <ConfirmDialog open={confirme === 'SUPPRIMER'} danger title="Supprimer ce lien ?"
        confirmLabel="Supprimer définitivement"
        onConfirm={() => void action('SUPPRIMER')} onClose={() => setConfirme(null)}>
        <p>Ce lien n’a aucun clic : sa suppression est autorisée. L’adresse courte <strong>{link.slug}</strong> redeviendra disponible.</p>
      </ConfirmDialog>
    </>
  )
}

export function AcquisitionLinkDetailPage({ slug }: { slug: string }) {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback="/communication/liens" />
        <ToastProvider><DetailContent slug={slug} /></ToastProvider>
      </main>
    </Shell>
  )
}

/* ═══════════ PAGE CRÉATION — /communication/liens/nouveau ═══════════ */

const SOURCES_CHOIX = ['QR', 'NFC', 'WEB', 'EMAIL', 'SOCIAL', 'PRINT', 'OTHER'] as const

function ChampSource({ value, onChange, labels }: {
  value: string | null; onChange: (v: string | null) => void; labels: Record<string, string>
}) {
  return (
    <div className="acq-sources" role="group" aria-label="Source">
      <button type="button" className={`acq-source ${value === null ? 'acq-source--on' : ''}`}
        aria-pressed={value === null} onClick={() => onChange(null)}>
        <Tag size={14} aria-hidden="true" /> À classer plus tard
      </button>
      {SOURCES_CHOIX.map((s) => {
        const Icon = SOURCE_ICONS[s] ?? Tag
        return (
          <button key={s} type="button" className={`acq-source ${value === s ? 'acq-source--on' : ''}`}
            aria-pressed={value === s} onClick={() => onChange(s)}>
            <Icon size={14} aria-hidden="true" /> {labels[s] ?? s}
          </button>
        )
      })}
    </div>
  )
}

function CreationContent() {
  const toast = useToast()
  const [label, setLabel] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouche, setSlugTouche] = useState(false)
  const [url, setUrl] = useState('')
  const [source, setSource] = useState<string | null>(null)
  const [placement, setPlacement] = useState('')
  const [asset, setAsset] = useState('')
  const [campagne, setCampagne] = useState('')
  const [actif, setActif] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erreurChamp, setErreurChamp] = useState<string | null>(null)

  const slugEffectif = slugTouche ? slugifier(slug) : slugifier(label)

  // Destination proposée : le compte Instagram du club tel que RÉGLÉ dans
  // Intégrations, jamais un nom écrit en dur (le compte officiel a déjà
  // changé une fois). Réglage illisible → champ vide, l'exemple guide la
  // saisie ; une saisie déjà commencée n'est jamais écrasée.
  useEffect(() => {
    void apiGet<{ club_username: string | null } | { error: string; message?: string }>(
      '/api/v1/communication/integrations/instagram/account')
      .then((j) => {
        if (estErreurApi(j) || !j.club_username) return
        const proposee = `https://www.instagram.com/${j.club_username}/`
        setUrl((u) => (u === '' ? proposee : u))
      })
      .catch(() => undefined)
  }, [])

  const creer = async () => {
    setBusy(true); setErreurChamp(null)
    const payload: Record<string, unknown> = { label: label.trim(), url: url.trim(), active: actif }
    if (slugEffectif) payload['slug'] = slugEffectif
    if (source) payload['source'] = source
    if (placement.trim()) payload['placement'] = placement.trim()
    if (asset.trim()) payload['asset'] = asset.trim()
    if (campagne.trim()) payload['campaign_default'] = campagne.trim()
    const r = await executerCommande('CREATE_LINK', payload)
    setBusy(false)
    if (r.ok) {
      toast('ok', 'Lien créé — l’adresse courte est désormais verrouillée.')
      navigate(`/communication/liens/${String(r.result?.['slug'] ?? slugEffectif)}`)
    } else {
      setErreurChamp(r.human_error ?? 'Le tracker a refusé la création.')
    }
  }

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Nouveau lien</h1>
          <p className="lc-subtitle">Un support = un lien. L’adresse courte se choisit MAINTENANT — après création, elle est définitive.</p>
        </div>
      </header>
      <form className="acq-form lc-in" onSubmit={(e) => { e.preventDefault(); void creer() }}>
        <label className="acq-champ">
          <span>Nom</span>
          <input className="lc-input" value={label} maxLength={60} required
            placeholder="Bar ROOM 1 — puce NFC"
            onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label className="acq-champ">
          <span>Adresse courte</span>
          <input className="lc-input" value={slugTouche ? slug : slugEffectif}
            placeholder="bar-room-1-nfc" maxLength={40}
            onChange={(e) => { setSlugTouche(true); setSlug(e.target.value) }} />
          <span className="lc-hint acq-apercu">
            Future adresse : <strong>https://insta.liveclubrennes.com/{slugEffectif || '…'}</strong>
            <Lock size={11} aria-hidden="true" /> définitive après création
          </span>
        </label>
        <label className="acq-champ">
          <span>Destination</span>
          <input className="lc-input" type="url" value={url} required
            placeholder="https://…"
            onChange={(e) => setUrl(e.target.value)} />
          <span className="lc-hint">Une adresse https:// publique — Instagram aujourd’hui, mais aussi site, billetterie, Google…</span>
        </label>
        <div className="acq-champ">
          <span>Source</span>
          <ChampSource value={source} onChange={setSource} labels={{
            QR: 'QR Code', NFC: 'NFC', WEB: 'Web', EMAIL: 'Email', SOCIAL: 'Réseaux sociaux', PRINT: 'Imprimé', OTHER: 'Autre',
          }} />
        </div>
        <div className="acq-deuxchamps">
          <label className="acq-champ">
            <span>Emplacement</span>
            <input className="lc-input" value={placement} placeholder="Accueil, Bar Salon, Vestiaire…"
              onChange={(e) => setPlacement(e.target.value)} />
          </label>
          <label className="acq-champ">
            <span>Support</span>
            <input className="lc-input" value={asset} placeholder="Carte NFC #01, Affiche A3 entrée…"
              onChange={(e) => setAsset(e.target.value)} />
          </label>
        </div>
        <label className="acq-champ">
          <span>Campagne par défaut</span>
          <input className="lc-input" value={campagne} placeholder="nfc-permanent"
            onChange={(e) => setCampagne(e.target.value)} />
          <span className="lc-hint">Utilisée quand ce lien est ouvert sans paramètre ?campaign=. La valeur explicite dans l’URL gagne toujours.</span>
        </label>
        <label className="acq-champ acq-champ--ligne">
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
          <span>Actif dès la création</span>
        </label>
        {erreurChamp ? <p className="cm-safenote">{erreurChamp}</p> : null}
        <div className="acq-form__actions">
          <button type="submit" className="lc-btn lc-btn--primary" disabled={busy || !label.trim()}>
            {busy ? 'Création…' : 'Créer le lien'}
          </button>
          <Link href="/communication/liens" className="lc-btn lc-btn--ghost">Annuler</Link>
        </div>
      </form>
    </>
  )
}

export function AcquisitionLinkNewPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback="/communication/liens" />
        <ToastProvider><CreationContent /></ToastProvider>
      </main>
    </Shell>
  )
}

/* ═══════ PAGE ÉDITION — /communication/liens/:slug/modifier ═══════ */

interface Brouillon {
  label: string
  url: string
  active: boolean
  source: string | null
  placement: string
  asset: string
  campaign_default: string
}

function versBrouillon(l: LienAcq): Brouillon {
  return {
    label: l.label, url: l.url, active: l.active, source: l.source,
    placement: l.placement ?? '', asset: l.asset ?? '', campaign_default: l.campaign_default ?? '',
  }
}

function EditionContent({ slug }: { slug: string }) {
  const [link, setLink] = useState<LienAcq | null>(null)
  const [d, setD] = useState<Brouillon | null>(null)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(() => {
    void apiGet<{ link: LienAcq; source_labels: Record<string, string> } | { error: string; message?: string }>(
      `/api/v1/communication/acquisition/liens/${slug}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lien introuvable.'); return }
        setLink(j.link); setD(versBrouillon(j.link)); setLabels(j.source_labels)
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [slug])
  useEffect(() => { charger() }, [charger])

  const ref = link ? versBrouillon(link) : null
  const dirty = !!(d && ref && JSON.stringify(d) !== JSON.stringify(ref))

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer le lien',
    successMessage: 'Lien mis à jour sur le tracker.',
    onSave: async () => {
      if (!d || !link) return 'Rien à enregistrer.'
      const patch: Record<string, unknown> = {}
      if (d.label !== link.label) patch['label'] = d.label.trim()
      if (d.url !== link.url) patch['url'] = d.url.trim()
      if (d.active !== link.active) patch['active'] = d.active
      if (d.source !== link.source) patch['source'] = d.source
      if (d.placement !== (link.placement ?? '')) patch['placement'] = d.placement.trim() || null
      if (d.asset !== (link.asset ?? '')) patch['asset'] = d.asset.trim() || null
      if (d.campaign_default !== (link.campaign_default ?? '')) patch['campaign_default'] = d.campaign_default.trim() || null
      const r = await executerCommande('UPDATE_LINK', { slug, patch })
      if (r.ok) { charger(); return true }
      // §37 : STALE_VERSION → le worker a déjà resynchronisé le miroir ; on
      // recharge, l'utilisateur repart des dernières informations.
      if (r.error_code === 'STALE_VERSION') charger()
      return r.human_error ?? 'Le tracker a refusé la modification.'
    },
    onCancel: () => { if (link) setD(versBrouillon(link)) },
  })

  if (erreur) return <p className="lc-hint">{erreur}</p>
  if (!link || !d) return null

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Modifier — {link.label}</h1>
          <p className="acq-permanente">
            <Lock size={12} aria-hidden="true" />
            <span>{link.public_url.replace('https://', '')}</span>
            <span className="lc-hint">adresse permanente, non modifiable</span>
          </p>
        </div>
      </header>
      <form className="acq-form lc-in" onSubmit={(e) => e.preventDefault()}>
        <label className="acq-champ">
          <span>Nom</span>
          <input className="lc-input" value={d.label} maxLength={60}
            onChange={(e) => setD({ ...d, label: e.target.value })} />
        </label>
        <label className="acq-champ">
          <span>Destination</span>
          <input className="lc-input" type="url" value={d.url}
            onChange={(e) => setD({ ...d, url: e.target.value })} />
        </label>
        <div className="acq-champ">
          <span>Source</span>
          <ChampSource value={d.source} onChange={(v) => setD({ ...d, source: v })} labels={labels} />
          {link.source === 'UNKNOWN_LEGACY' ? (
            <span className="lc-hint">« Historique indéterminé » : l’origine de ces clics ne pourra jamais être établie — c’est un état définitif, différent de « pas encore renseigné ».</span>
          ) : null}
        </div>
        <div className="acq-deuxchamps">
          <label className="acq-champ">
            <span>Emplacement</span>
            <input className="lc-input" value={d.placement} placeholder="Accueil, Bar Salon, Vestiaire…"
              onChange={(e) => setD({ ...d, placement: e.target.value })} />
          </label>
          <label className="acq-champ">
            <span>Support</span>
            <input className="lc-input" value={d.asset} placeholder="Carte NFC #01, Sticker QR vestiaire…"
              onChange={(e) => setD({ ...d, asset: e.target.value })} />
          </label>
        </div>
        <label className="acq-champ">
          <span>Campagne par défaut</span>
          <input className="lc-input" value={d.campaign_default}
            onChange={(e) => setD({ ...d, campaign_default: e.target.value })} />
          <span className="lc-hint">Utilisée quand ce lien est ouvert sans paramètre ?campaign=. La valeur explicite dans l’URL gagne toujours.</span>
        </label>
        {link.is_root ? (
          <p className="lc-hint"><Link2 size={12} aria-hidden="true" /> Lien racine : il ne peut être ni désactivé ni supprimé — sa destination reste modifiable.</p>
        ) : (
          <label className="acq-champ acq-champ--ligne">
            <input type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} />
            <span>Lien actif</span>
          </label>
        )}
      </form>
    </>
  )
}

export function AcquisitionLinkEditPage({ slug }: { slug: string }) {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback={`/communication/liens/${slug}`} />
        <ToastProvider><EditionContent slug={slug} /></ToastProvider>
      </main>
    </Shell>
  )
}
